import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  mergeNotifications,
  normalizeNotification,
  selectNewNotifications,
  shouldAnnounceNotification,
  summarizeNotifications,
} from "../notifications/notificationData";
import { emitNexusFabricEvent } from "../lib/nexusEventBus";

const AlertContext = createContext(null);
const ALERTS_STORAGE_KEY = "hqs:notifications:v2";
const LEGACY_ALERTS_STORAGE_KEY = "hqs-alerts";
const PREFERENCES_STORAGE_KEY = "hqs:notification-preferences:v2";
const LEGACY_PREFERENCES_STORAGE_KEY = "hqs-alert-preferences";
const MAX_ALERTS = 200;
const MAX_TOASTS = 4;

const DEFAULT_PREFERENCES = Object.freeze({
  inAppAlerts: true,
  browserPush: false,
  minimumPriority: "info",
  agentAlerts: true,
  systemAlerts: true,
  emailAlerts: false,
  email: "",
  priceAlerts: true,
  signalAlerts: true,
  scoreAlerts: true,
});

function readJson(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function loadAlerts() {
  const current = readJson(ALERTS_STORAGE_KEY, null);
  const legacy = current ?? readJson(LEGACY_ALERTS_STORAGE_KEY, []);
  return mergeNotifications([], Array.isArray(legacy) ? legacy : [], MAX_ALERTS);
}

function loadPreferences() {
  const current = readJson(PREFERENCES_STORAGE_KEY, null);
  const legacy = current ?? readJson(LEGACY_PREFERENCES_STORAGE_KEY, {});
  return { ...DEFAULT_PREFERENCES, ...(legacy && typeof legacy === "object" ? legacy : {}) };
}

export function AlertProvider({ children }) {
  const [alerts, setAlerts] = useState(loadAlerts);
  const [alertPreferences, setAlertPreferences] = useState(loadPreferences);
  const [toastAlerts, setToastAlerts] = useState([]);
  const alertsRef = useRef(alerts);

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  useEffect(() => {
    try {
      window.localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(alerts));
    } catch {
      // The live notification layer remains available when storage is blocked.
    }
  }, [alerts]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(alertPreferences));
    } catch {
      // Preferences remain active for the current session.
    }
  }, [alertPreferences]);

  const queueToasts = useCallback((items) => {
    const announceable = items.filter((item) => shouldAnnounceNotification(item, alertPreferences));
    if (announceable.length === 0) return;
    setToastAlerts((current) => mergeNotifications(current, announceable, MAX_TOASTS));
  }, [alertPreferences]);

  const publishNotification = useCallback((input, options = {}) => {
    const normalized = normalizeNotification(input, { source: options.source });
    const current = alertsRef.current;
    const isNew = selectNewNotifications(current, [normalized]).length > 0;
    const next = mergeNotifications(current, [normalized], MAX_ALERTS);
    alertsRef.current = next;
    setAlerts(next);
    if (isNew) {
      emitNexusFabricEvent({
        type: normalized.category || "notification",
        source: normalized.source || "notification_layer",
        sourceId: normalized.sourceId || normalized.id,
        title: normalized.title,
        detail: normalized.message,
        priority: normalized.priority,
        createdAt: normalized.createdAt,
        targetView: normalized.action?.targetView,
        metadata: normalized.metadata,
      });
      if (options.announce !== false) queueToasts([normalized]);
    }
    return normalized;
  }, [queueToasts]);

  const ingestNotifications = useCallback((incoming, options = {}) => {
    const safeIncoming = Array.isArray(incoming) ? incoming : [];
    const current = alertsRef.current;
    const newItems = selectNewNotifications(current, safeIncoming);
    const next = mergeNotifications(current, safeIncoming, MAX_ALERTS);
    alertsRef.current = next;
    setAlerts(next);
    newItems.forEach((item) => emitNexusFabricEvent({
      type: item.category || "notification",
      source: item.source || "notification_layer",
      sourceId: item.sourceId || item.id,
      title: item.title,
      detail: item.message,
      priority: item.priority,
      createdAt: item.createdAt,
      targetView: item.action?.targetView,
      metadata: item.metadata,
    }));
    if (options.announce !== false) queueToasts(newItems);
  }, [queueToasts]);

  const addAlert = useCallback((symbol, type, message) => publishNotification({
    symbol,
    type,
    message,
    source: "local_market",
    sourceId: `${symbol || "system"}:${type || "alert"}:${Date.now()}`,
    priority: type === "error" ? "high" : "medium",
    targetView: symbol ? "intelligence" : "pulse",
    metadata: symbol ? { symbol } : {},
  }), [publishNotification]);

  const markAsRead = useCallback((id) => {
    setAlerts((current) => current.map((alert) => (
      alert.id === id ? { ...alert, read: true, seenAt: alert.seenAt || new Date().toISOString() } : alert
    )));
  }, []);

  const markAllAsRead = useCallback(() => {
    const now = new Date().toISOString();
    setAlerts((current) => current.map((alert) => ({ ...alert, read: true, seenAt: alert.seenAt || now })));
  }, []);

  const markAsActed = useCallback((id) => {
    const now = new Date().toISOString();
    setAlerts((current) => current.map((alert) => (
      alert.id === id ? { ...alert, read: true, seenAt: alert.seenAt || now, actedAt: now } : alert
    )));
  }, []);

  const dismissAlert = useCallback((id) => {
    const now = new Date().toISOString();
    setAlerts((current) => current.map((alert) => (
      alert.id === id ? { ...alert, read: true, dismissedAt: now } : alert
    )));
    setToastAlerts((current) => current.filter((alert) => alert.id !== id));
  }, []);

  const dismissToast = useCallback((id) => {
    setToastAlerts((current) => current.filter((alert) => alert.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
    setToastAlerts([]);
  }, []);

  const getUnreadCount = useCallback(() => summarizeNotifications(alerts).unread, [alerts]);

  const updatePreferences = useCallback((preferences) => {
    setAlertPreferences((current) => ({ ...current, ...preferences }));
  }, []);

  const summary = useMemo(() => summarizeNotifications(alerts), [alerts]);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const updateBadge = async () => {
      try {
        if (summary.unread > 0 && typeof navigator.setAppBadge === "function") {
          await navigator.setAppBadge(summary.unread);
        } else if (summary.unread === 0 && typeof navigator.clearAppBadge === "function") {
          await navigator.clearAppBadge();
        }
      } catch {
        // App badges are progressive enhancement and are not supported by every browser.
      }
    };
    void updateBadge();
  }, [summary.unread]);

  const value = useMemo(() => ({
    alerts,
    alertPreferences,
    toastAlerts,
    summary,
    addAlert,
    publishNotification,
    ingestNotifications,
    markAsRead,
    markAllAsRead,
    markAsActed,
    dismissAlert,
    dismissToast,
    clearAlerts,
    getUnreadCount,
    updatePreferences,
  }), [
    alerts,
    alertPreferences,
    toastAlerts,
    summary,
    addAlert,
    publishNotification,
    ingestNotifications,
    markAsRead,
    markAllAsRead,
    markAsActed,
    dismissAlert,
    dismissToast,
    clearAlerts,
    getUnreadCount,
    updatePreferences,
  ]);

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
}

export function useAlerts() {
  const context = useContext(AlertContext);
  if (!context) throw new Error("useAlerts must be used within AlertProvider");
  return context;
}
